import { AOI, LatLong, MapLayer } from '../../../models/Geometries';
//@ts-ignore
import XMLParser from 'react-xml-parser';
import { AtributosRegistros, GeoserverFeaturesSearch } from '../../../models/Geoserver';
import { convertCoordinatesToEPSG3857 } from '../../../utils/GeometryUtils';
import axios from 'axios';
import { interceptor } from '../../../utils/axios.interceptor';
import { atributosRegistrosEndPoint } from '../../../utils/endPoints';

const api = axios.create();
interceptor(api);

const geoserverAxios = axios.create({
  auth: {
    username: process.env.REACT_APP_GEOSERVER_USER ?? '',
    password: process.env.REACT_APP_GEOSERVER_PASSWORD ?? ''
  },
  baseURL: process.env.REACT_APP_GEOSERVER_URL
});

export const fetchAllLayers = async (): Promise<MapLayer[]> => {
  const response = await geoserverAxios.get(
    '/wms?service=WMS&version=1.3.0&request=GetCapabilities', {
      transformResponse: undefined,
    }
  );
  const text = response.data;

  const xml: any = new XMLParser().parseFromString(text, 'text/xml');
  const layerObjects: any =
    xml.children[1].children[2].children[2].children.filter(
      (c: any) => c.name === 'Layer',
    );
  const layersList: MapLayer[] = layerObjects.map((layer: any) => {
    const bounds: any = layer.children.find(
      (c: any) => c.name === 'BoundingBox',
    ).attributes;
    const keywords: string[] = layer.children.find((c: any) => c.name === 'KeywordList')?.children?.map((c: any) => c.value)
    const type = keywords?.find(k => k.startsWith('TYPE:'))?.split('TYPE:');
    let timeAttribute: any = keywords
      ?.find(k => k.startsWith('TIME_ATTRIBUTE:'))
      ?.split('TIME_ATTRIBUTE:');
    timeAttribute = timeAttribute && timeAttribute[1] ? timeAttribute[1] : null;
    if(!type){
      return null;
    }
    return {
      name: layer.children.find((c: any) => c.name === 'Name')?.value,
      title: layer.children.find((c: any) => c.name === 'Title')?.value,
      bounds: [bounds.minx, bounds.miny, bounds.maxx, bounds.maxy].map(
        (b: string) => parseFloat(b),
      ),
      type: keywords?.find(k => k.startsWith('TYPE:'))?.split('TYPE:')[1],
      timeAttribute: timeAttribute
    };
  });
  return layersList.filter(l => l != null).sort((a, b) => a.title.localeCompare(b.title));

};

export const fetchFeaturesInArea = async (
  layer: MapLayer,
  southWestBound: LatLong,
  northEastBound: LatLong,
  fetchXlsAttributes = false
): Promise<GeoserverFeaturesSearch> => {
  const response = await geoserverAxios.get(
    '/wfs?version=2.0.0&request=GetFeature' +
    `&typeNames=${layer.name}&service=wfs&outputFormat=application/json` +
    `&srsName=EPSG:4326&bbox=` +
    `${southWestBound.longitud},${southWestBound.latitud},` +
    `${northEastBound.longitud},${northEastBound.latitud},EPSG:4326` ,
  );
  const features: GeoserverFeaturesSearch = response.data;
  if(fetchXlsAttributes){
    await loadXlsAttributes(features)
  }
  return { ...features, layer };
};

// Hace un fetch attributes para un solo click.
// Similar al que busca por area, pero optimizado para funcionar con areas pequeñas
// (solo la zona donde se hace click)
export const fetchFeatureAttributes = async (
  layer: MapLayer,
  southWestBound: LatLong,
  northEastBound: LatLong,
  fetchXlsAttributes = false
): Promise<GeoserverFeaturesSearch> => {
  const response = await geoserverAxios.get(
    '/wms?service=WMS&version=1.1.1&request=GetFeatureInfo&exceptions=application/json&' +
    `layers=${layer.name}&query_layers=${layer.name}&` +
    `x=0&y=0&height=1&width=1&srs=EPSG:4326&bbox=` +
    `${southWestBound.longitud},${southWestBound.latitud},` +
    `${northEastBound.longitud},${northEastBound.latitud}` +
    '&info_format=application/json&feature_count=10',
  );
  const features: GeoserverFeaturesSearch = response.data;
  if(fetchXlsAttributes){
    await loadXlsAttributes(features)
  }
  return { ...features, layer };
};

//Por cada feature traida de geoserver, busca por numero de expediente en el api
//atributos extra provenientes de los archivos xls cargados (DJA y RPM)
const loadXlsAttributes = async (features: GeoserverFeaturesSearch) => {
  const promises = [];
  const featuresWithExpediente = features.features.filter(f => f.properties?.expediente);
  for (const feature of featuresWithExpediente) {
        const promise = api.get<AtributosRegistros>(
          atributosRegistrosEndPoint + '/' + feature.properties.expediente,
        ).catch(e => {});
        promises.push(promise);
  }
  const responses = await Promise.all(promises);
  for(const response of responses){
    if(response?.data && response.data.numExp){
      features.features
        .find(f => f.properties.expediente === response.data.numExp)!
        .atriutosRegistros = response.data;
    }
  }
}


export const getLayerUrl = (layer: MapLayer) => {
  const geoserverUrl = process.env.REACT_APP_GEOSERVER_URL;
  //solo le agregamos el parametro tiempo a productos satelitales
  const addTimeParam = layer.type === process.env.REACT_APP_GEOSERVER_WMS_ONLY_LAYER;
  return geoserverUrl + '/wms?' +
    'SERVICE=WMS&' +
    'VERSION=1.3.0&' +
    'REQUEST=GetMap&' +
    'FORMAT=image/png&' +
    `LAYERS=${layer.name}&` +
    'TRANSPARENT=TRUE&' +
    'SRS=EPSG:3857&' +
    'CRS=EPSG:3857&' +
    'TILED=true&' +
    'WIDTH=256&' +
    'HEIGHT=256&' +
    (layer.timeAttribute ? ('sortBy=' + layer.timeAttribute + '&') : '') +
    (addTimeParam ? `TIME=P3M/PRESENT&` : '') +
    'BBOX={bbox-epsg-3857}'
}

export const getAOIImage = (aoi: AOI) => {
  const x = Math.abs(Math.abs(aoi.bounds[0]) - Math.abs(aoi.bounds[2])) * 0.78;
  const y = Math.abs(Math.abs(aoi.bounds[1]) - Math.abs(aoi.bounds[3]));
  const bound1 = convertCoordinatesToEPSG3857(aoi.bounds[0], aoi.bounds[1]);
  const bound2 = convertCoordinatesToEPSG3857(aoi.bounds[2], aoi.bounds[3]);
  return process.env.REACT_APP_GEOSERVER_URL +
    `/${process.env.REACT_APP_GEOSERVER_WORKSPACE}/wms?service=WMS&version=1.1.0&request=GetMap&layers=${aoi.layers.map(l => l.name).join(',')}` +
    `&bbox=${[...bound1, ...bound2].join(',')}&transparent=true&width=${Math.round((2000) * (x / y))}&height=${2000}&` +
    `srs=EPSG:3857&styles=&format=image%2Fpng&TIME=${process.env.REACT_APP_TIME_RANGE_GEOSERVER_LAYERS}`+
    `&sortBy=${
    aoi.layers
      .map(l => (l.timeAttribute ? l.timeAttribute : ''))
      .map(v => `(${v})`)
      .join('')}`;

}

